import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';

import sha256 from 'crypto-js/sha256';
import { Subscription } from 'rxjs';
import { AppUtilityService } from 'src/app/app-utility.service';
import { UserService } from 'src/app/core/services/lmsuser.service';

import { NotificationService } from '../../notification/notification.service';
import { SlidePanelService } from './slide-panel.service';

export type PanelSide = 'left' | 'right';
interface Quote {
  quoteId: number;
  loanStatus: string;
  branchSerial: string;
  idNumber: string;
  recent?: boolean;
}

@Component({
  selector: 'lms-slide-panel',
  templateUrl: './slide-panel.component.html',
  styleUrls: ['./slide-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideInOut', [
      state('open', style({ transform: 'translateX(0%)' })),
      state('closed-left', style({ transform: 'translateX(-95%)' })),
      state('closed-right', style({ transform: 'translateX(95%)' })),
      // state('peek', style({ transform: 'translateX(93%)' })), // for right side
      state('peek-left', style({ transform: 'translateX(-80%)' })), // for left side
      transition('closed-left => peek-left', animate('150ms ease-out')),
      transition('peek-left => closed-left', animate('100ms ease-in')),
      transition('* => *', animate('300ms ease-in-out')),
    ]),
    trigger('statusPulse', [
      state('normal', style({ transform: 'scale(1)', backgroundColor: '*' })),
      state(
        'pulsed',
        style({ transform: 'scale(.7)', backgroundColor: 'transparent' }),
      ),
      transition('normal => pulsed', [animate('150ms ease-in')]),
      transition('pulsed => normal', [animate('300ms ease-out')]),
    ]),
  ],
})
export class SlidePanelComponent implements OnInit, OnDestroy {
  /** Controls visibility of panel */

  @Input() isOpen = false;

  @Input() canUsePanel = true;

  @Input() allowPinning = true;
  @Output() alertRaised = new EventEmitter<{
    type: string;
    message: string;
    target: string;
  }>();

  isPinned = false;

  statusAnimationState: 'normal' | 'pulsed' | 'none' = 'normal';

  /** Side of the panel: 'left' or 'right' */

  @Input() panelside: PanelSide = 'left';

  /** Width of the panel (e.g. '300px') */

  @Input() panelWidth = '300px';

  /** Emits when backdrop is clicked */

  @Output() closed = new EventEmitter<void>();

  /** Emits when a new item is added (for notifications) */

  @Output() newItemAdded = new EventEmitter<any>();

  @Input() allowedRoles: string[] = [];

  public myForm = this.fb.group({
    name: ['', Validators.required],
  });

  hasAlerts = false;
  isAdmin = false;
  badgeCount = 0;
  lastMessage = '';

  /** Core subscriptions (panel state/canUse) — never cleared by stopNotificationListeners */
  private coreSubscriptions: Subscription[] = [];
  /** Notification-only subscriptions — cleared/recreated when canUsePanel changes */
  private subscriptions: Subscription[] = [];

  constructor(
    private panelService: SlidePanelService,
    public global_utility: AppUtilityService,
    private fb: FormBuilder,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private userService: UserService,
  ) {}

  ngOnInit() {
    this.restorePinState();
  }
  private restorePinState() {
    this.closePanel();
    const pinned = localStorage.getItem('panelPinned') === 'true';
    this.isPinned = pinned;
    if (this.isPinned) this.isOpen = true;

    // stateSub goes into coreSubscriptions so stopNotificationListeners() never kills it
    const stateSub = this.panelService.state$.subscribe((opened) => {
      console.log('Panel state changed:', opened);
      this.isOpen = opened;
      this.cdr.markForCheck();
    });
    this.coreSubscriptions.push(stateSub);

    // canUsePanel$ is a BehaviorSubject — callback fires synchronously here,
    // which would call stopNotificationListeners() and wipe this.subscriptions.
    // Using coreSubscriptions keeps it safe from that teardown.
    const canUseSub = this.panelService.canUsePanel$.subscribe((canuse) => {
      console.log('Panel canUse changed:', canuse);
      if (canuse) {
        this.setupNotificationListeners();
      } else {
        this.stopNotificationListeners();
      }
      this.canUsePanel = canuse;
      this.cdr.markForCheck();
    });
    this.coreSubscriptions.push(canUseSub);
  }

  private setupNotificationListeners() {
    const badgeSub = this.notificationService.badge$.subscribe((count) => {
      this.setBadgeCount(count);
    });
    this.subscriptions.push(badgeSub);

    try {
      this.notificationService.startQuotePolling((updatedQuote) => {
        // React to quote status change (e.g. update store, show animation)
        if (updatedQuote && Array.isArray(updatedQuote)) {
          this.handleStatusChange(updatedQuote);
        } else {
          console.warn('Received invalid quote data:', updatedQuote);
        }
      });
    } catch (error) {
      console.error('Failed to start quote polling:', error);
    }

    const streamSub = this.notificationService.stream$.subscribe((event) => {
      if (event.target === 'alerts-panel') {
        this.lastMessage = event.message;
      }
    });
    this.subscriptions.push(streamSub);
  }

  ngOnDestroy() {
    this.stopNotificationListeners();
    this.coreSubscriptions.forEach((sub) => sub.unsubscribe());
    this.coreSubscriptions = [];
  }

  private stopNotificationListeners() {
    console.log('Stopping notification listeners for slide panel');
    this.notificationService.stopPolling();
    // Only clears notification subscriptions, never coreSubscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];
  }

  public quotes: any[] = [];
  private previousHashes = new Map<number, string>(); // id → hash

  handleStatusChange(quotes: Quote[]): void {
    if (!quotes || !Array.isArray(quotes)) return;

    const bmbranchSerial = this.global_utility.getGlobalData('branch_serial');
    const branchName = this.userService?.userBranchName?.toLowerCase() || '';
    const isCallCentreUser = branchName.includes('eersterivier 1');

    let filteredQuotes = quotes;

    if (!isCallCentreUser) {
      if (bmbranchSerial) {
        filteredQuotes = filteredQuotes.filter(
          (q) => q.branchSerial === bmbranchSerial,
        );
      }
    }

    filteredQuotes = filteredQuotes.filter((quote) => {
      // Use lowercase for both to ensure they match
      const status = (
        quote.loanStatus ||
        (quote as any).status ||
        ''
      ).toLowerCase();
      const targetStatus = 'aulapproval';

      if (isCallCentreUser) {
        // Call Centre sees ONLY AULApproval (from ANY branch)
        return status === targetStatus;
      } else {
        // Branch Managers see everything EXCEPT AULApproval (for THEIR branch)
        return status !== targetStatus;
      }
    });

    this.quotes = [...filteredQuotes];

    // 3. Update hashes and "recent" flags
    this.quotes = filteredQuotes.map((q) => {
      const hash = sha256(
        `${q.quoteId}:${q.loanStatus}:${q.branchSerial}`,
      ).toString();
      const prev = this.previousHashes.get(q.quoteId);
      const isNew = !prev || hash !== prev;
      this.previousHashes.set(q.quoteId, hash);
      return { ...q, recent: isNew };
    });

    // 4. Notifications and Cleanup
    this.quotes.forEach((item) => {
      if (item.recent) {
        setTimeout(() => (item.recent = false), 10000);
        if (item.quoteId && item.idNumber) {
          this.addItem(item.quoteId);
          this.raisePanelAlert(
            `Application: ${item.quoteId} requires approval!`,
          );
        }
      }
    });

    this.notificationService.setBadgeCount(this.quotes.length);
    this.cdr.markForCheck();
  }

  get showBadge(): boolean {
    return !this.isOpen && !this.isPinned && this.newNotify;
  }

  setBadgeCount(count: number) {
    this.badgeCount = count;
    this.cdr.markForCheck(); // ensure re-render under OnPush
  }

  /** Set explicit visibility (useful for route-based toggling) */

  onPanelAlert(event: { type: string; message: string; target: string }) {
    if (event.target === 'alerts-panel') {
      this.badgeCount++;
      this.lastMessage = event.message;
    }
    this.raisePanelAlert(event.message);
    //this.snackbar.open(`⚠️ ${event.message}`, 'Dismiss', { duration: 3000 });
  }

  raisePanelAlert(message: string) {
    this.notificationService.notifytarget({
      type: 'info',
      target: 'alerts-panel',
      message,
    });
  }

  togglePanel(event?: MouseEvent) {
    console.log('togglePanel called', {
      isPinned: this.isPinned,
      isOpen: this.isOpen,
      event,
    });

    // skip toggle if click originated from inside content
    if (
      event?.target &&
      (event.target as HTMLElement).closest('.content-zone')
    ) {
      console.log('Click from content-zone, skipping toggle');
      return;
    }

    // Don't toggle if pinned
    if (this.isPinned) {
      console.log('Panel is pinned, skipping toggle');
      return;
    }

    console.log('Toggling panel via service');
    this.panelService.toggle();
    // Change detection is already triggered by the subscription
  }

  /** Escape key closes panel */

  @HostListener('document:keydown.escape')
  onEsc() {
    if (this.isOpen) {
      this.closePanel();
      this.cdr.markForCheck();
    }
  }

  /** Backdrop click handler */

  onBackdropClick() {
    this.closePanel();
  }

  pinPanel() {
    this.isPinned = true;
    this.isOpen = true;
    this.panelService.setPanelStatus(true);
    localStorage.setItem('panelPinned', 'true');
    this.cdr.markForCheck();
  }

  unpinPanel() {
    this.isPinned = false;
    this.panelService.setPanelStatus(false);
    this.isOpen = false;
    localStorage.setItem('panelPinned', 'false');
    this.cdr.markForCheck();
  }
  get tooltipMessage() {
    return `⚡${this.badgeCount} Awaiting`;
  }

  /** Helper to close and emit event */

  private closePanel() {
    this.isOpen = false;
    this.panelService.close();
    this.closed.emit();
    this.cdr.markForCheck();
  }

  /** Dynamically resolve animation state */

  get animationState(): string {
    if (this.isOpen || this.isPinned) return 'open';
    if (this.hoverPreview)
      return this.panelside === 'left' ? 'peek-left' : 'peek';
    return this.panelside === 'left' ? 'closed-left' : 'closed-right';
  }
  get newNotify(): boolean {
    return this.badgeCount > 0 && !this.isOpen && !this.isPinned;
  }
  hoverPreview = false;

  @HostListener('mouseenter')
  onHoverEnter() {
    this.hoverPreview = true;
    this.cdr.markForCheck();
  }

  @HostListener('mouseleave')
  onHoverLeave() {
    this.hoverPreview = false;
    this.cdr.markForCheck();
  }

  onPanelOpened() {
    this.notificationService.clearBadge();
  }

  /** Example method: trigger item-added notification */

  addItem(item: any) {
    this.newItemAdded.emit(item);
  }

  get canAccess(): boolean {
    return this.canUsePanel;
  }
}
